/**
 * ルート定義（05-ui.md §5.2）。
 *
 * ファイルベースではなく手組みのルートツリーにしている。生成物（routeTree.gen.ts）が
 * 無い方が Docker の bind mount + polling 環境で事故が少なく、
 * ルートの一覧性も保てるため。
 *
 * 認証ガードは pathless なレイアウトルート（`appRoute`）の `beforeLoad` に 1 か所だけ置く。
 * 各画面が個別にログイン判定を書くと、必ずどこかで抜ける。
 */
import type { FunctionComponent } from 'react';
import {
  createRootRoute,
  createRoute,
  createRouter,
  lazyRouteComponent,
  Outlet,
} from '@tanstack/react-router';
import { APP_SHELL_ROUTER_SCROLL_OPTIONS } from '@mail-sv/ui';
import { AppLayout, NotFoundPage } from './components/app-layout.js';
import { bootstrapSession } from './lib/auth.js';
import { authStore } from './stores/auth.js';
import { redirectTo } from './lib/nav.js';

import { LoginPage } from './routes/login.js';
import { LegalIndexPage, LegalDocumentPage } from './routes/legal.js';
import { SignupPage, SignupVerifyPage } from './routes/signup.js';
import { InvitePage, ResetPasswordPage } from './routes/reset-password.js';
import { WebmailLoginPage } from './routes/webmail-login.js';
import { ForwardVerifyPage } from './routes/forward-verify.js';
import { ConfirmEmailPage } from './routes/confirm-email.js';
import { ImpersonatePage } from './routes/impersonate.js';
import { DashboardPage } from './routes/dashboard.js';
import { DomainsPage } from './routes/domains.js';
import { DomainDetailPage } from './routes/domain-detail.js';
import { MailboxesPage } from './routes/mailboxes.js';
import { MailboxDetailPage } from './routes/mailbox-detail.js';
import { AliasesPage } from './routes/aliases.js';
import { PrivateAddressesPage } from './routes/private-addresses.js';
import { AutoresponderPage } from './routes/autoresponder.js';
import { SpamPage } from './routes/spam.js';
import { AiPage } from './routes/ai.js';
import { PrivacyPage } from './routes/privacy.js';
import { MailClientsPage } from './routes/mail-clients.js';
import { UsagePage } from './routes/usage.js';
import { LogsPage } from './routes/logs.js';
import { SecurityPage } from './routes/security.js';
import { BillingPage } from './routes/billing.js';
import { BillingPlanPage } from './routes/billing-plan.js';
import { MembersPage } from './routes/members.js';
import { ApiTokensPage } from './routes/api-tokens.js';
import { SettingsPage } from './routes/settings.js';
import { WebmailPage } from './routes/webmail.js';
import { ExportsPage } from './routes/exports.js';
import { BrandingPage } from './routes/branding.js';
import { NotificationsPage } from './routes/notifications.js';
import { SupportPage, SupportTicketPage } from './routes/support.js';
import { HelpPage } from './routes/help.js';
import { JobsPage } from './routes/jobs.js';
import { PrintSetupSheetPage } from './routes/print-setup-sheet.js';

/**
 * 一斉配信の画面は**遅延読み込みにする**（[57]）。
 *
 * 判断の根拠は実測値である。2026-08-07 時点の `vite build` では、
 * 初期チャンク 744.27 kB（gzip 230.71 kB）に対して、
 * ここで切り出した一斉配信の画面群が 186.9 kB（gzip 74.9 kB）ある。
 * 分割しなければ単一チャンクは 931 kB（gzip 306 kB）になり、
 * Vite の 900 kB 警告に乗る。この 187 kB は
 * **一斉配信を使わない契約者にも全員が払っている**。
 * 一斉配信は上位プランの機能で、メールボックスだけの契約者はサイドナビにも
 * 出ない（`bulkNavGroup()` が権限ゼロなら null を返す）。
 *
 * `React.lazy` ではなく `lazyRouteComponent()` を使うのは、
 * ルーターの `defaultPreload: 'intent'` と噛み合うため。リンクにマウスを
 * 乗せた時点で取りに行くので、体感上の待ちはほぼ生じない。
 * また、デプロイでチャンク名が変わったときに「古いハッシュを取りに行って
 * 白画面」にならない再読み込み処理が入っている（素の `React.lazy` には無い）。
 *
 * 逆に**メールの基本機能（ドメイン・メールボックス等）は分割しない**。
 * 全契約者が必ず通る画面を遅延にすると、初回の描画に往復が 1 つ増えるだけで
 * 誰の役にも立たない。
 */
const ContentPage = lazyRouteComponent(() => import('./routes/content.js'), 'ContentPage');
const ContentEditorPage = lazyRouteComponent(
  () => import('./routes/content-editor.js'),
  'ContentEditorPage',
);
const ContentPreviewPage = lazyRouteComponent(
  () => import('./routes/content-preview.js'),
  'ContentPreviewPage',
);
const MediaPage = lazyRouteComponent(() => import('./routes/media.js'), 'MediaPage');
const CampaignsPage = lazyRouteComponent(() => import('./routes/campaigns.js'), 'CampaignsPage');
const CampaignDetailPage = lazyRouteComponent(
  () => import('./routes/campaign-detail.js'),
  'CampaignDetailPage',
);
const ListsPage = lazyRouteComponent(() => import('./routes/lists.js'), 'ListsPage');
const WebhooksPage = lazyRouteComponent(() => import('./routes/webhooks.js'), 'WebhooksPage');
const ReviewsPage = lazyRouteComponent(() => import('./routes/reviews.js'), 'ReviewsPage');
const LibraryPage = lazyRouteComponent(() => import('./routes/library.js'), 'LibraryPage');
const BulkSettingsPage = lazyRouteComponent(
  () => import('./routes/bulk-settings.js'),
  'BulkSettingsPage',
);
const SendingStreamsPage = lazyRouteComponent(
  () => import('./routes/sending-streams.js'),
  'SendingStreamsPage',
);
const SignupFormsPage = lazyRouteComponent(
  () => import('./routes/signup-forms.js'),
  'SignupFormsPage',
);

const rootRoute = createRootRoute({
  component: () => <Outlet />,
  notFoundComponent: NotFoundPage,
});

const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/login',
  component: LoginPage,
  beforeLoad: async () => {
    // 既にログイン済みならログイン画面を見せない（ブックマーク経由の再訪対策）。
    await bootstrapSession();
    if (authStore.getState().status === 'authenticated') redirectTo('/');
  },
});

/**
 * 法定ページ（R0 §2.1）。**`rootRoute` の直下＝認証不要**。
 * `appRoute` の下に置くと「規約を読むためにログインが要る」ことになり、
 * 特商法 11 条の広告表示にもならない。
 */
const legalIndexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/legal',
  component: LegalIndexPage,
});

const legalDocumentRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/legal/$slug',
  component: function LegalDocumentRoute() {
    const { slug } = legalDocumentRoute.useParams();
    return <LegalDocumentPage slug={slug} />;
  },
});

/**
 * セルフサインアップ（R0 §2.2）。**認証不要**。
 * `/signup/verify` は確認メールのリンク先で、ログイン前に必ず通る。
 */
const signupRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/signup',
  component: SignupPage,
});

const signupVerifyRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/signup/verify',
  component: SignupVerifyPage,
});

/**
 * パスワード再設定と招待の受諾。**認証不要**（`rootRoute` 直下）。
 *
 * ここを `appRoute` の下に置くと「ログインするためにログインが要る」ことになる。
 * `POST /auth/portal/password/forgot` のメール本文が指す URL は
 * `${PORTAL_ORIGIN}/reset-password?token=...` で固定なので、パスは変えないこと
 * （変えるとメールのリンクが全部 404 になる）。
 */
const resetPasswordRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/reset-password',
  // 既定は再設定モード。プロパティを渡さない形にしてルーターの型に合わせる。
  component: function ResetPasswordRoute() {
    return <ResetPasswordPage />;
  },
});

const inviteRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/invite',
  component: InvitePage,
});

/**
 * Webメールの SSO ログイン（認証不要）。OIDC の authorize がここへ 302 で送る。
 * **アプリのレイアウトを持たせない** —— まだ誰でもないので、
 * サイドナビや組織切り替えを出す文脈が無い。`/invite` と同じ扱い。
 */
const webmailLoginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/webmail-login',
  component: WebmailLoginPage,
});

/**
 * メール転送の同意確認。**認証不要**（転送先はこの製品の利用者とは限らない）。
 *
 * `routes/portal/aliases.ts` が送る確認メールの `verifyUrl` が
 * `${PORTAL_ORIGIN}/forwards/verify?token=...` で固定なので、パスは変えないこと。
 */
const forwardVerifyRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/forwards/verify',
  component: ForwardVerifyPage,
});

/**
 * ログインメールアドレス変更の確認。**認証不要**（別端末でメールを開く前提）。
 *
 * `routes/auth/common.ts` の `POST /me/email` が送る確認メールの URL が
 * `${PORTAL_ORIGIN}/confirm-email?token=...` で固定なので、パスは変えないこと。
 */
const confirmEmailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/confirm-email',
  component: ConfirmEmailPage,
});

/**
 * 代理ログインの受け口。**認証不要**（この画面でセッションを確立する）。
 *
 * `routes/ops/orgs.ts` が返す `portalUrl` が
 * `${PORTAL_ORIGIN}/impersonate#access_token=...` で固定なので、パスは変えないこと。
 */
const impersonateRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/impersonate',
  component: ImpersonatePage,
});

/**
 * 認証必須のレイアウト。`id` 指定の pathless route なので URL には現れない。
 */
const appRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: 'app',
  component: AppLayout,
  beforeLoad: async ({ location }) => {
    await bootstrapSession();
    if (authStore.getState().status !== 'authenticated') {
      // ログイン後に元の画面へ戻せるよう、遷移先を持たせる。
      redirectTo('/login', { redirect: location.pathname + location.searchStr });
    }
  },
});

/**
 * 印刷用の画面。**認証は要るがアプリのレイアウトは着けない。**
 *
 * 設定情報シートは紙に印刷して人に渡す成果物なので、サイドナビ・ヘッダ・
 * パンくずが同じ木の上にあると、それらを消す `@media print` を全画面ぶん
 * 書くことになる。レイアウトの外に出して、印刷のことを知っているのを
 * この画面 1 枚だけにする。ガードは `appRoute` と同じものを使う。
 */
const printRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: 'print',
  component: () => <Outlet />,
  beforeLoad: async ({ location }) => {
    await bootstrapSession();
    if (authStore.getState().status !== 'authenticated') {
      redirectTo('/login', { redirect: location.pathname + location.searchStr });
    }
  },
});

const printSetupSheetRoute = createRoute({
  getParentRoute: () => printRoute,
  path: '/print/mail-setup',
  component: PrintSetupSheetPage,
});

/**
 * ルーターは関数コンポーネントしか受け付けない（クラスコンポーネントは非対応）。
 * `lazyRouteComponent()` の戻り値は「関数コンポーネント + `preload()`」なので
 * ここにそのまま渡せる。
 */
function page(path: string, component: FunctionComponent) {
  return createRoute({ getParentRoute: () => appRoute, path, component });
}

/**
 * 遅延読み込み中の表示。
 *
 * **スピナー単体は使わない**（05-ui.md §11.2）。レイアウトが崩れないよう、
 * 画面の骨格に近い形のプレースホルダを出す。
 */
function RouteLoading() {
  return (
    <div className="space-y-4" aria-busy="true" aria-live="polite">
      <div className="h-8 w-1/3 animate-pulse rounded-md bg-panel" />
      <div className="h-4 w-2/3 animate-pulse rounded-md bg-panel" />
      <div className="h-64 animate-pulse rounded-lg bg-panel" />
    </div>
  );
}

const routes = [
  page('/', DashboardPage),
  page('/domains', DomainsPage),
  page('/domains/$domainId', DomainDetailPage),
  // 05-ui.md §8.2 の「最重要画面」の URL。実体はドメイン詳細の DNS タブ。
  page('/domains/$domainId/dns', DomainDetailPage),
  /*
   * 証明書タブを URL で名指しできるようにする。メールソフト設定の画面から
   * 「このドメインの独自ホスト名が使えるようになったか」へ直接飛ばすため
   * （飛び先が DNS タブだと契約者が自分でタブを探すことになる）。
   */
  page('/domains/$domainId/certificate', DomainDetailPage),
  page('/mailboxes', MailboxesPage),
  page('/mailboxes/$mailboxId', MailboxDetailPage),
  page('/aliases', AliasesPage),
  /*
   * プライベートメールアドレス。**遅延読み込みにしない。**
   * 「迷惑メールが来た瞬間に止める」ための画面なので、チャンクの取得を
   * 挟むと最も急いでいるときに待たされる（一斉配信の画面群とは性質が違う）。
   */
  page('/private-addresses', PrivateAddressesPage),
  page('/autoresponder', AutoresponderPage),
  page('/spam', SpamPage),
  /*
   * **AI と受信者プライバシー。** どちらも API（`portal/ai.ts` /
   * `portal/privacy.ts`）は揃っていたのに画面が 1 枚も無く到達不能だった。
   * 「メール本文を LLM へ渡すかどうか」と「受信者を追跡から守るかどうか」は
   * どちらも契約者自身が決めるべきことなので、運営面だけに置かない。
   */
  page('/ai', AiPage),
  page('/privacy', PrivacyPage),
  page('/mail-clients', MailClientsPage),
  page('/usage', UsagePage),
  page('/logs', LogsPage),
  page('/security', SecurityPage),
  page('/billing', BillingPage),
  page('/billing/plan', BillingPlanPage),
  page('/members', MembersPage),
  page('/api-tokens', ApiTokensPage),
  page('/settings', SettingsPage),
  page('/exports', ExportsPage),
  page('/branding', BrandingPage),
  page('/webmail', WebmailPage),

  /**
   * 一斉配信（CMS_DESIGN.md §6.1）。
   * `/bulk` 単体はキャンペーン一覧を出す。パンくずの「配信」から辿れる先が
   * 存在しないと 404 になるため、独立した概要画面を作らずに割り当てている。
   */
  page('/bulk', CampaignsPage),
  page('/bulk/campaigns', CampaignsPage),
  page('/bulk/campaigns/$campaignId', CampaignDetailPage),
  page('/bulk/contents', ContentPage),
  page('/bulk/contents/$contentId', ContentEditorPage),
  page('/bulk/contents/$contentId/preview', ContentPreviewPage),
  page('/bulk/media', MediaPage),
  page('/bulk/lists', ListsPage),
  page('/bulk/webhooks', WebhooksPage),
  // 承認待ちキューと組織のポリシー設定（[55]）。
  page('/bulk/reviews', ReviewsPage),
  page('/bulk/settings', BulkSettingsPage),
  // タグ / 予約 / 取り込み / 書き出しなど、組織全体に効く運用ツール（[54]）。
  page('/bulk/library', LibraryPage),
  /*
   * 送信元ストリームと申込フォーム。どちらも API は揃っていたのに画面が
   * 1 枚も無く、到達不能だった。ストリームは **verified になるまで一斉配信
   * が出せない**ので、キャンペーン画面からここへ誘導する。
   */
  page('/bulk/streams', SendingStreamsPage),
  page('/bulk/signup-forms', SignupFormsPage),

  // 通知・障害情報・問い合わせ。
  /*
   * **製品の中のヘルプ。** ヘッダの「ヘルプ」は `VITE_SUPPORT_URL` が
   * 設定されているときだけ外部サイトへ出る導線で、未設定の構成では
   * どこからもヘルプに辿り着けなかった。
   */
  page('/help', HelpPage),
  page('/notifications', NotificationsPage),
  page('/support', SupportPage),
  page('/support/tickets/$ticketId', SupportTicketPage),
  page('/jobs', JobsPage),
];

const routeTree = rootRoute.addChildren([
  loginRoute,
  legalIndexRoute,
  legalDocumentRoute,
  // より具体的な `/signup/verify` を先に置く（TanStack Router は静的セグメントを
  // 優先するので順序に依存しないが、読み手のために並べておく）。
  signupVerifyRoute,
  signupRoute,
  resetPasswordRoute,
  inviteRoute,
  webmailLoginRoute,
  forwardVerifyRoute,
  confirmEmailRoute,
  impersonateRoute,
  printRoute.addChildren([printSetupSheetRoute]),
  appRoute.addChildren(routes),
]);

export const router = createRouter({
  routeTree,
  defaultPreload: 'intent',
  // データ取得は TanStack Query に一本化しているので、ルーター側のキャッシュは使わない。
  defaultPreloadStaleTime: 0,
  defaultNotFoundComponent: NotFoundPage,
  // 分割したチャンクを取りに行っている間の表示。
  defaultPendingComponent: RouteLoading,
  // 一瞬で解決したときに骨格が点滅しないよう、少し待ってから出す。
  defaultPendingMs: 200,
  /*
   * スクロールするのは window ではなく AppShell の main。新しい画面では main も先頭へ戻し、
   * 戻る / 進むでは離れたときの位置へ戻す。以前は何も指定しておらず、main は DOM ごと
   * 使い回されるので、長い一覧の下のほうから移った画面も下のほうから始まっていた。
   */
  ...APP_SHELL_ROUTER_SCROLL_OPTIONS,
});
